import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";

import { getDecryptedAccessToken } from "../../Utils/LoginUserDetails.js/LoginUserSavedDetails";
import ApiServices from "../../Utils/ApiServices/ApiServices";

const COUNTRY_CODES = [
  { code: "IN", label: "India", dial_code: "+91" },
  { code: "US", label: "United States", dial_code: "+1" },
  { code: "GB", label: "United Kingdom", dial_code: "+44" },
  { code: "CA", label: "Canada", dial_code: "+1" },
  { code: "AU", label: "Australia", dial_code: "+61" },
  { code: "AE", label: "United Arab Emirates", dial_code: "+971" },
  { code: "SA", label: "Saudi Arabia", dial_code: "+966" },
  { code: "SG", label: "Singapore", dial_code: "+65" },
  { code: "MY", label: "Malaysia", dial_code: "+60" },
  { code: "NZ", label: "New Zealand", dial_code: "+64" },
  { code: "DE", label: "Germany", dial_code: "+49" },
  { code: "FR", label: "France", dial_code: "+33" },
  { code: "IT", label: "Italy", dial_code: "+39" },
  { code: "ES", label: "Spain", dial_code: "+34" },
  { code: "ZA", label: "South Africa", dial_code: "+27" },
  { code: "PK", label: "Pakistan", dial_code: "+92" },
  { code: "BD", label: "Bangladesh", dial_code: "+880" },
  { code: "NP", label: "Nepal", dial_code: "+977" },
  { code: "LK", label: "Sri Lanka", dial_code: "+94" },
  { code: "QA", label: "Qatar", dial_code: "+974" },
];

const DEFAULT_THEME = {
  primaryColor: "#1C7C6C",
  secondaryColor: "#E8F7F3",
  buttonColor: "#1C7C6C",
  bgColor: "#F5F8FB",
  cardColor: "#FFFFFF",
  inputBg: "#FFFFFF",
  title: "Create Your Account",
  subtitle: "Register to save your details and enjoy a faster ordering experience.",
  buttonText: "Create Account",
};

const RestaurantCustomerRegisterForm = () => {
  const token = getDecryptedAccessToken();

  const [formData, setFormData] = useState({
    client_id: "CLT1001",
    country_code: "+1",
    phone: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [pageTheme, setPageTheme] = useState(DEFAULT_THEME);
  const [loading, setLoading] = useState(false);
  const [registeredData, setRegisteredData] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );

  const sanitizeColor = (value, fallback) => {
    if (!value) return fallback;
    const trimmed = String(value).trim();
    const isHex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(trimmed);
    const isRgb = /^rgb(a?)\((.*?)\)$/.test(trimmed);
    return isHex || isRgb ? trimmed : fallback;
  };

  useEffect(() => {
    const client_id = searchParams.get("client_id") || "";
    const primaryColor = sanitizeColor(
      searchParams.get("primaryColor"),
      DEFAULT_THEME.primaryColor
    );
    const secondaryColor = sanitizeColor(
      searchParams.get("secondaryColor"),
      DEFAULT_THEME.secondaryColor
    );
    const buttonColor = sanitizeColor(
      searchParams.get("buttonColor"),
      DEFAULT_THEME.buttonColor
    );
    const bgColor = sanitizeColor(
      searchParams.get("bgColor"),
      DEFAULT_THEME.bgColor
    );
    const cardColor = sanitizeColor(
      searchParams.get("cardColor"),
      DEFAULT_THEME.cardColor
    );
    const inputBg = sanitizeColor(
      searchParams.get("inputBg"),
      DEFAULT_THEME.inputBg
    );
    const title = searchParams.get("title") || DEFAULT_THEME.title;
    const subtitle = searchParams.get("subtitle") || DEFAULT_THEME.subtitle;
    const buttonText = searchParams.get("buttonText") || DEFAULT_THEME.buttonText;
    const defaultCountryCode = searchParams.get("countryCode") || "+91";
    const defaultClientId = searchParams.get("client_id") || "CLT1001";

    setFormData((prev) => ({
      ...prev,
      client_id: defaultClientId,
      country_code: defaultCountryCode,
    }));

    setPageTheme({
      primaryColor,
      secondaryColor,
      buttonColor,
      bgColor,
      cardColor,
      inputBg,
      title,
      subtitle,
      buttonText,
    });
  }, [searchParams]);

  const selectedCountry = useMemo(() => {
    return (
      COUNTRY_CODES.find(
        (item) => item.dial_code === formData.country_code
      ) || COUNTRY_CODES[0]
    );
  }, [formData.country_code]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const nameRegex = /^[A-Za-z]+(?:[ '-][A-Za-z]+)*$/;
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  const getFullPhone = () => {
    const cleanedPhone = String(formData.phone || "").replace(/\D/g, "");
    return `${formData.country_code}${cleanedPhone}`;
  };

  const validateField = (name, value) => {
    switch (name) {
      // case "client_id":
      //   if (!String(value || "").trim()) return "Client ID is missing.";
      //   return "";

      case "first_name":
        if (!String(value || "").trim()) return "First name is required.";
        if (String(value).trim().length < 2) return "Minimum 2 characters required.";
        if (!nameRegex.test(String(value).trim())) return "Enter a valid first name.";
        return "";

      case "last_name":
        if (!String(value || "").trim()) return "Last name is required.";
        if (String(value).trim().length < 2) return "Minimum 2 characters required.";
        if (!nameRegex.test(String(value).trim())) return "Enter a valid last name.";
        return "";

      case "email":
        if (!String(value || "").trim()) return "";
        if (!emailRegex.test(String(value).trim())) return "Enter a valid email address.";
        return "";

      case "country_code":
        if (!String(value || "").trim()) return "Country code is required.";
        return "";

      case "phone": {
        const digits = String(value || "").replace(/\D/g, "");
        if (!digits) return "Phone number is required.";
        if (digits.length < 6 || digits.length > 15) {
          return "Phone number must be 6 to 15 digits.";
        }
        return "";
      }

      case "password":
        if (!String(value || "").trim()) return "";
        if (String(value).length < 6) return "Password must be at least 6 characters.";
        return "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {
      // client_id: validateField("client_id", formData.client_id),
      first_name: validateField("first_name", formData.first_name),
      last_name: validateField("last_name", formData.last_name),
      email: validateField("email", formData.email),
      country_code: validateField("country_code", formData.country_code),
      phone: validateField("phone", formData.phone),
      password: validateField("password", formData.password),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleInputChange = (field, value) => {
    let updatedValue = value;

    if (field === "phone") {
      updatedValue = String(value || "").replace(/\D/g, "");
    }

    if (field === "first_name" || field === "last_name") {
      updatedValue = String(value || "")
        .replace(/[^A-Za-z\s'-]/g, "")
        .replace(/\s{2,}/g, " ");
    }

    if (field === "email") {
      updatedValue = String(value || "").trimStart();
    }

    setFormData((prev) => ({
      ...prev,
      [field]: updatedValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: validateField(field, updatedValue),
    }));
  };

  const buildPayload = () => {
    const payload = {
      client_id: formData.client_id,
      phone: getFullPhone(),
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
    };

    if (formData.email.trim()) {
      payload.email = formData.email.trim().toLowerCase();
    }

    if (formData.password.trim()) {
      payload.password = formData.password;
    }

    return payload;
  };

  const postWithFallback = async (url, payload, headers) => {
    if (ApiServices && typeof ApiServices.post === "function") {
      return await ApiServices.post(url, payload, { headers });
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(data?.detail || "Something went wrong.");
      error.response = { data, status: response.status };
      throw error;
    }

    return { data, status: response.status };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar("Please fix the highlighted fields.", "error");
      return;
    }

    try {
      setLoading(true);

      const payload = buildPayload();
      const baseUrl = process.env.REACT_APP_SERVERURL || "";

      if (!baseUrl) {
        throw new Error("API base URL is missing.");
      }

      const endpoint = `/api/v1/customers/register/`;

      const headers = {
        "Content-Type": "application/json",
      };

      // if (token) {
      //   headers.Authorization = `Bearer ${token}`;
      // }

      const response = await postWithFallback(endpoint, payload);
      const data = response?.data || response;

      setRegisteredData(data);

      showSnackbar("Registration completed successfully.", "success");

      setFormData((prev) => ({
        ...prev,
        phone: "",
        first_name: "",
        last_name: "",
        email: "",
        password: "",
      }));

      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          {
            type: "restaurant_customer_registered",
            success: true,
            client_id: payload.client_id,
            customer: data?.customer || null,
            customer_id: data?.customer_id || null,
          },
          "*"
        );
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        "Registration failed. Please try again.";

      showSnackbar(errorMessage, "error");

      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          {
            type: "restaurant_customer_registered",
            success: false,
            client_id: formData.client_id,
            error: errorMessage,
          },
          "*"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const commonTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      borderRadius: "14px",
      backgroundColor: pageTheme.inputBg,
      transition: "all 0.25s ease",
      "& fieldset": {
        borderColor: "rgba(15, 23, 42, 0.12)",
      },
      "&:hover fieldset": {
        borderColor: pageTheme.primaryColor,
      },
      "&.Mui-focused fieldset": {
        borderColor: pageTheme.primaryColor,
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: pageTheme.primaryColor,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: "4px",
    },
  };


  

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${pageTheme.bgColor} 0%, ${pageTheme.secondaryColor} 100%)`,
        display: "flex",
        alignItems: "center",
        py: { xs: 1.5, sm: 2.5, md: 4 },
        px: { xs: 1, sm: 2 },
      }}
    >
      <Container maxWidth="sm" sx={{ px: { xs: 0.5, sm: 1 } }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: { xs: 3, sm: 4 },
            overflow: "hidden",
            backgroundColor: pageTheme.cardColor,
            border: `1px solid ${pageTheme.secondaryColor}`,
            boxShadow: "0 18px 50px rgba(15, 23, 42, 0.10)",
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(135deg, ${pageTheme.primaryColor} 0%, ${pageTheme.buttonColor} 100%)`,
              color: "#fff",
              px: { xs: 2, sm: 3.5 },
              py: { xs: 2.5, sm: 3.5 },
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: { xs: 44, sm: 52 },
                  height: { xs: 44, sm: 52 },
                  borderRadius: "14px",
                  backgroundColor: "rgba(255,255,255,0.16)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <StorefrontRoundedIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "1.2rem", sm: "1.55rem" },
                    lineHeight: 1.2,
                  }}
                >
                  {pageTheme.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.92,
                    mt: 0.75,
                    fontSize: { xs: "0.9rem", sm: "0.95rem" },
                    lineHeight: 1.5,
                  }}
                >
                  {pageTheme.subtitle}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange("first_name", e.target.value)}
                    error={Boolean(errors.first_name)}
                    helperText={errors.first_name || " "}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    sx={commonTextFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineRoundedIcon sx={{ color: pageTheme.primaryColor }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange("last_name", e.target.value)}
                    error={Boolean(errors.last_name)}
                    helperText={errors.last_name || " "}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    sx={commonTextFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineRoundedIcon sx={{ color: pageTheme.primaryColor }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={COUNTRY_CODES}
                    autoHighlight
                    value={selectedCountry}
                    onChange={(event, newValue) => {
                      handleInputChange(
                        "country_code",
                        newValue?.dial_code || "+91"
                      );
                    }}
                    getOptionLabel={(option) =>
                      `${option.label} (${option.dial_code})`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.code === value.code &&
                      option.dial_code === value.dial_code
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country Code"
                        error={Boolean(errors.country_code)}
                        helperText={errors.country_code || " "}
                        sx={commonTextFieldSx}
                      />
                    )}
                    sx={{
                      ...commonTextFieldSx,
                      "& .MuiOutlinedInput-root": {
                        ...commonTextFieldSx["& .MuiOutlinedInput-root"],
                        pr: 1,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Phone Number"
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    error={Boolean(errors.phone)}
                    helperText={errors.phone || "Enter digits only"}
                    fullWidth
                    inputProps={{
                      inputMode: "numeric",
                      maxLength: 15,
                    }}
                    sx={commonTextFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIphoneRoundedIcon sx={{ color: pageTheme.primaryColor }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Email Address"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    error={Boolean(errors.email)}
                    helperText={errors.email || "Optional"}
                    fullWidth
                    inputProps={{ maxLength: 100 }}
                    sx={commonTextFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlinedIcon sx={{ color: pageTheme.primaryColor }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    error={Boolean(errors.password)}
                    helperText={
                      errors.password ||
                      "Optional. Leave blank for OTP-only account if supported."
                    }
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    sx={commonTextFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon sx={{ color: pageTheme.primaryColor }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: { xs: 1.75, sm: 2 },
                      borderRadius: 3,
                      backgroundColor: pageTheme.secondaryColor,
                      border: `1px solid ${pageTheme.primaryColor}22`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#334155",
                        lineHeight: 1.7,
                        fontSize: { xs: "0.88rem", sm: "0.93rem" },
                      }}
                    >
                      By registering, your details will be created directly under
                      this restaurant account for faster future ordering.
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{
                      minHeight: 54,
                      borderRadius: "14px",
                      fontSize: "1rem",
                      fontWeight: 700,
                      textTransform: "none",
                      backgroundColor: pageTheme.buttonColor,
                      boxShadow: "0 10px 25px rgba(0,0,0,0.12)",
                      "&:hover": {
                        backgroundColor: pageTheme.primaryColor,
                      },
                    }}
                  >
                    {loading ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                        <span>Please wait...</span>
                      </Stack>
                    ) : (
                      pageTheme.buttonText
                    )}
                  </Button>
                </Grid>

                {registeredData?.customer ? (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        borderRadius: 3,
                        p: 2,
                        border: "1px solid #D1FAE5",
                        backgroundColor: "#F0FDF4",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        sx={{ color: "#166534", mb: 1 }}
                      >
                        Registration successful
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#166534" }}>
                        Customer: {registeredData.customer.first_name}{" "}
                        {registeredData.customer.last_name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#166534" }}>
                        Phone: {registeredData.customer.phone}
                      </Typography>
                      {registeredData.customer.email ? (
                        <Typography variant="body2" sx={{ color: "#166534" }}>
                          Email: {registeredData.customer.email}
                        </Typography>
                      ) : null}
                    </Box>
                  </Grid>
                ) : null}
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RestaurantCustomerRegisterForm;